// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import {
  ActionableNotification,
  Button,
  InlineLoading,
  ProgressIndicator,
  ProgressStep,
  Tile,
} from '@carbon/react';
import { Link } from 'react-router-dom';
import SiteProfileStep from './steps/SiteProfileStep';
import DocumentsStep from './steps/DocumentsStep';
import ReviewApproveStep from './steps/ReviewApproveStep';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useCoverageSession } from '../../hooks/useCoverageSession';
import { updateSiteProfile } from '../../services/coverageService';
import { getUserData } from '../../services/tokenStorage';
import type { CoverageSession, SiteProfile } from '../../types';
import './OnboardingWizard.css';

const STEPS = [
  { label: 'Site profile', hint: 'Identify the site' },
  { label: 'Documents', hint: 'Upload and track coverage' },
  { label: 'Review & approve', hint: 'Publish staged results' },
];

/** Session states that mean the run has moved past document collection. */
const REVIEW_STATUSES = new Set([
  'processing',
  'processing_failed',
  'staged',
  'in_review',
  'publishing',
  'published',
  'partially_published',
]);

/** Derive the wizard's resume step from the active session: no session or no
 * profile yet means step one; a saved profile means documents; a submitted
 * session means review. Pure so a reload always lands where the user left. */
export function deriveStep(session: CoverageSession | null): 0 | 1 | 2 {
  if (!session) return 0;
  if (REVIEW_STATUSES.has(session.status)) return 2;
  return session.site_profile?.site_name ? 1 : 0;
}

function WizardBody({ siteId }: { siteId: string }) {
  const { session, loading, error, create, refetch, refresh } = useCoverageSession(siteId);
  const [step, setStep] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Step state initializes from the session once resolved; afterwards the
  // user drives navigation (completed steps stay clickable).
  const currentStep = step ?? deriveStep(session);

  const saveProfile = async (profile: SiteProfile, andContinue: boolean) => {
    setSaving(true);
    setSaveError(null);
    try {
      let active = session;
      if (!active) {
        active = await create({ created_by: getUserData()?.email });
      }
      await updateSiteProfile(siteId, active.session_id, profile);
      refetch();
      if (andContinue) setStep(1);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <InlineLoading description="Resolving onboarding session..." />;

  return (
    <>
      {error && (
        <ActionableNotification
          kind="error"
          title="Coverage service unavailable"
          subtitle={error}
          lowContrast
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
        />
      )}
      <ProgressIndicator
        currentIndex={currentStep}
        spaceEqually
        className="obw-progress"
        data-testid="onboarding-progress"
        onChange={(index: number) => {
          // Completed steps stay clickable; forward jumps go through the
          // step actions (save, submit) so state transitions stay explicit.
          if (index < currentStep) setStep(index);
        }}
      >
        {STEPS.map((stepDef, index) => (
          <ProgressStep
            key={stepDef.label}
            label={stepDef.label}
            secondaryLabel={stepDef.hint}
            complete={index < currentStep}
            current={index === currentStep}
            disabled={index > currentStep}
          />
        ))}
      </ProgressIndicator>

      {currentStep === 0 && (
        <SiteProfileStep
          key={session?.session_id ?? 'new'}
          session={session}
          saving={saving || Boolean(error)}
          error={saveError}
          onSave={saveProfile}
        />
      )}
      {currentStep === 1 && session && (
        <DocumentsStep
          siteId={siteId}
          session={session}
          refetchSession={refresh}
          onAdvance={() => {
            refetch();
            setStep(2);
          }}
        />
      )}
      {currentStep === 2 && session && (
        <ReviewApproveStep
          siteId={siteId}
          sessionId={session.session_id}
          onBack={() => setStep(1)}
        />
      )}
    </>
  );
}

/** Site onboarding wizard (design source: Helios Bindings mock, "Onboard
 * new" tab). Three steps against the coverage service; keyed by site so
 * switching sites fully resets wizard state. */
export default function OnboardingWizard() {
  const { siteId, siteLabel, isFleet } = useActiveSite();

  return (
    <div className="obw-page" data-testid="onboarding">
      <div className="obw-header">
        <h2>Site onboarding</h2>
        <span className="obw-subnote">
          {isFleet ? 'Whole fleet' : siteLabel} · manifest-driven document coverage
        </span>
      </div>

      {isFleet ? (
        <Tile className="obw-placeholder">
          <p>Select a site from the header to onboard it.</p>
          <p>
            Creating a brand new site record instead?{' '}
            <Link to="/onboarding/new-site">
              <Button kind="tertiary" size="sm">
                New site setup
              </Button>
            </Link>
          </p>
        </Tile>
      ) : (
        <WizardBody key={siteId} siteId={siteId} />
      )}
    </div>
  );
}
